import _ from '../vendors/_';
import {Point, WinU_state} from '../../@types/shared';
import Ticker from './Ticker';

const tmpEl: HTMLElement = document.createElement('div');
const data: { [key: number]: object } = {};
const uniqueIds: HTMLElement[] = [];

export const $ = (selector: any): DomU => {
  let el;
  if (_.isElement(selector) || selector === window) el = selector;
  else if (_.isString(selector)) el = $.find(_.trim(selector))[0];
  else console.error('[DomU] selector must be HTMLElement | string');
  return new DomU(el);
};

$.uniqueId = (el: HTMLElement): Number => {
  const ind: number = uniqueIds.indexOf(el);
  if (ind >= 0) return ind + 1;
  uniqueIds.push(el);
  return uniqueIds.length;
};

$.data = (el: HTMLElement, key: string, value?: any) => {
  const id = String($.uniqueId(el));
  data[id] = data[id] || {};
  if (value === undefined) return data[id][key];
  data[id][key] = value;
};

$.find = (selector: string, parent?: HTMLElement): HTMLElement[] => {
  if (selector.substr(0, 1) === '#' && selector.indexOf(' ') < 0) return _.compact([document.getElementById(selector.substr(1))]);
  if (selector.substr(0, 1) === '<') {
    tmpEl.innerHTML = selector;
    return [<HTMLElement>tmpEl.children[0]];
  }
  if (parent) return _.map(parent.querySelectorAll(selector));
  return _.map(document.querySelectorAll(selector));
};

$.find$ = (selector: string, parent?: HTMLElement): DomU[] => {
  return $.find(selector, parent).map((el) => $(el));
};


export default class DomU {

  _el: HTMLElement;
  _eventListeners: { [key: string]: Function[] } = {};
  _isVideoMetaLoaded: boolean;
  _videoWidth: number;
  _videoHeight: number;
  _delayId: number;

  get el(): HTMLElement {
    return this._el;
  }

  get isVideoMetaLoaded(): boolean {
    return this._isVideoMetaLoaded;
  }

  get videoWidth(): number {
    return this._videoWidth;
  }

  get videoHeight(): number {
    return this._videoHeight;
  }

  constructor(el: HTMLElement) {
    this._el = el;
  }

  dispose() {
    const ind: number = uniqueIds.indexOf(this._el);
    uniqueIds[ind] = null;//連番が狂うので削除でなくnullを突っ込むだけ
  }

  public data(key: string): any;
  public data(key: string, value: any): this;
  public data(key: string, value?: any) {
    if (value === undefined) return $.data(this._el, key);
    $.data(this._el, key, value);
    return this;
  }

  public find(selector: string): HTMLElement[] {
    return $.find(selector, this._el);
  }

  public find$(selector: string): DomU[] {
    return $.find$(selector, this._el);
  }

  public on(e: string, fn: Function, useCapture: any = false): this {
    e.split(' ').forEach((e: string) => {
      if (!this._eventListeners[e]) this._eventListeners[e] = [];
      this._eventListeners[e].push(fn);
      this._el.addEventListener(e, <EventListenerOrEventListenerObject>fn, useCapture);
    });
    return this;
  }

  public off(e: string, fn?: Function): this {
    _.forEach(this._eventListeners, (fns, _e) => {
      if (e && e.indexOf(_e) < 0) return;
      fns.forEach((_fn, i: number) => {
        if (fn && fn !== _fn) return;
        this._el.removeEventListener(_e, _fn);
        fns[i] = null;
      });
      this._eventListeners[_e] = _.compact(fns);
      if (this._eventListeners[_e].length === 0) delete this._eventListeners[_e];
    });
    return this;
  }

  public onClick(fn: Function) {
    this.on('click', (e)=> {
      e.preventDefault();
      e.stopPropagation();
      fn(e);
    });
  }

  public trigger(e: string, detail = {}): this {
    if ((<any>window).CustomEvent) {
      this._el.dispatchEvent(new CustomEvent(e, detail));
    } else {
      const ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(e, true, true, detail);
      this._el.dispatchEvent(ev);
    }
    return this;
  }

  public onImgLoad(complete: Function, error?: Function): this {
    const img: HTMLImageElement = <HTMLImageElement>this._el;
    if (img.complete || (<any>img).readyState == 'complete' || (<any>img).readyState == 4) {
      setTimeout(complete, 0);//callback内でthisが拾えない問題対応
      return this;
    }
    this
      .on('load', complete)
      .on('error', (err) => {
        console.error(`image load error [${img.src}]`, err, img);
        if (error) error();
        // else complete(err);
      });
    return this;
  }

  onVideoMetaLoad(complete: Function, error?: Function) {
    const video: HTMLVideoElement = <HTMLVideoElement>this._el;
    if (this._isVideoMetaLoaded || video.readyState >= 2) {
      this._isVideoMetaLoaded = true;
      setTimeout(complete, 0);//callback内でthisが拾えない問題対応
      return this;
    }
    if (video.videoWidth > 0) {
      this._isVideoMetaLoaded = true;
      this._videoWidth = video.videoWidth;
      this._videoHeight = video.videoHeight;
      complete();
      return this;
    }
    this
      .on('loadedmetadata', () => {
        this._isVideoMetaLoaded = true;
        this._videoWidth = video.videoWidth;
        this._videoHeight = video.videoHeight;
        complete();
      })
      .on('error', (err) => {
        console.error(`video load error [${video.src}]`, err, video);
        if (error) error();
        else complete();
      });
    return this;
  }

  public remove() {
    if (this._el.parentNode) this._el.parentNode.removeChild(this._el);
    return this;
  }

  width(): number {
    return this._el.offsetWidth;
  }

  height(): number {
    return this._el.offsetHeight;
  }

  innerWidth(): number {
    return this._el.offsetWidth - parseInt(this.css('padding-left') || '0') - parseInt(this.css('padding-right') || '0');
  }

  scrollWidth(): number {
    return this._el.scrollWidth;
  }

  scrollHeight(): number {
    return this._el.scrollHeight;
  }

  naturalWidth(): number {
    return (<HTMLImageElement>this._el).width;
  }

  naturalHeight(): number {
    return (<HTMLImageElement>this._el).height;
  }

  append(child: (HTMLElement | DomU)): this {
    if (child instanceof DomU) child = child.el;
    this._el.appendChild(child);
    return this;
  }

  appendTo(parent: (HTMLElement | DomU)): this {
    if (parent instanceof DomU) parent = parent.el;
    parent.appendChild(this._el);
    return this;
  }

  prepend(child: (HTMLElement | DomU)): this {
    if (child instanceof DomU) child = child.el;
    this._el.insertBefore(child, this._el.firstChild);
    return this;
  }

  parent(): Node {
    return this._el.parentNode;
  }

  addClass(className: string): this {
    className.split(' ').forEach((c: string) => {
      this._el.classList.add(c);
    });
    return this;
  }

  hasClass(className: string): boolean {
    return this._el.classList.contains(className);
  }

  removeClass(className: string): this {
    className.split(' ').forEach((c: string) => {
      this._el.classList.remove(c);
    });
    return this;
  }

  toggleClass(className: string, add: boolean): this {
    return add ? this.addClass(className) : this.removeClass(className);
  }

  css(ruleName: string): string;
  css(ruleName: string, value?: any): this;
  css(ruleName: object): this;
  css(ruleName, value?) {
    if (_.isString(ruleName)) {
      //get
      if (value === undefined) return <string>getComputedStyle(this._el)[<string>ruleName];

      //set
      this._applyCSS(ruleName, value);
    } else {
      //set（objectによる一括）
      _.forEach(ruleName, (v, k) => {
        this._applyCSS(k, v);
      });
    }
    return this;
  }


  /**
   * css3のtransform設定
   * @param props ex) {x:100, y:100, scaleX: 0.6, scaleY: 0.3}
   */
  transform(props?: { x?: (number | string), y?: (number | string), z?: (number | string), scale?: (number | string), scaleX?: (number | string), scaleY?: (number | string), rotate?: (number | string), rotateX?: (number | string), rotateY?: (number | string), rotateZ?: (number | string), skewX?: (number | string), perspective?: (number | string) }): this {
    let transforms = [];
    if (props) {
      if (props.perspective !== undefined) transforms.push(`perspective(${_.isString(props.perspective) ? props.perspective : props.perspective + 'px'})`);
      if (props.x !== undefined || props.y !== undefined || props.z !== undefined) {
        const x = (props.x && _.isString(props.x)) ? props.x : (props.x || 0) + 'px';
        const y = (props.y && _.isString(props.y)) ? props.y : (props.y || 0) + 'px';
        const z = (props.z && _.isString(props.z)) ? props.z : (props.z || 0) + 'px';
        transforms.push(`translate3d(${x}, ${y}, ${z})`);
      }
      if (props.scale !== undefined || props.scaleX !== undefined || props.scaleY !== undefined) transforms.push(`scale(${props.scale || props.scaleX || 0}, ${props.scale || props.scaleY || 0})`);
      if (props.rotate !== undefined) transforms.push(`rotate(${props.rotate || 0}deg)`);
      if (props.rotateX !== undefined) transforms.push(`rotateX(${props.rotateX || 0}deg)`);
      if (props.rotateY !== undefined) transforms.push(`rotateY(${props.rotateY || 0}deg)`);
      if (props.rotateZ !== undefined) transforms.push(`rotateZ(${props.rotateZ || 0}deg)`);
      if (props.skewX !== undefined) transforms.push(`skewX(${props.skewX || 0}deg)`);
    }
    this._el.style.transform = this._el.style['-webkit-transform'] = transforms.length > 0 ? transforms.join(' ') : '';
    return this;
  }

  _applyCSS(ruleName: string, value) {
    //ハイフンつなぎをキャメルケースに変換
    if (ruleName.indexOf('-') >= 0) {
      ruleName = _.map(ruleName.split('-'), (p, i) => {
        if (i === 0) return p;
        return p.substr(0, 1).toUpperCase() + p.substr(1);
      }).join('');
    }

    //apply
    this._el.style[ruleName] = _.isString(value) || (ruleName === 'opacity') || (ruleName === 'zIndex') || (ruleName === 'strokeDashoffset') ? `${value}` : value + 'px';
  }

  scrollTop(): number;
  scrollTop(top: number): this;
  scrollTop(top?: number) {
    if (top === undefined) return this._el.scrollTop;
    this._el.scrollTop = top;
    return this;
  }

  scrollLeft(): number;
  scrollLeft(top: number): this;
  scrollLeft(top?: number) {
    if (top === undefined) return this._el.scrollLeft;
    this._el.scrollLeft = top;
    return this;
  }

  attr(name: string): string;
  attr(name: string, value: any): this;
  attr(name, value?: any) {
    if (value === undefined) return this._el.getAttribute(name);
    this._el.setAttribute(name, value);
    return this;
  }

  attrBool(name:string) {
    return parseInt(this.attr(name)) === 1;
  }

  val(): string;
  val(value: any): this;
  val(value?: any) {
    if (value === undefined) return (<HTMLInputElement>this._el).value;
    (<HTMLInputElement>this._el).value = value;
    return this;
  }

  html(): string;
  html(html: string): this;
  html(html?: string) {
    if (html === undefined) return this._el.innerHTML;
    this._el.innerHTML = html;
    return this;
  }

  removeAttr(name: string): this {
    this._el.removeAttribute(name);
    return this;
  }

  clientRect(): ClientRect {
    return this._el.getBoundingClientRect();
  }

  position(winUState: WinU_state): Point {
    const rect = this.clientRect();
    return {
      x: rect.left + winUState.scrollLeft,
      y: rect.top + winUState.scrollTop,
    };
  }

  rawPosition(): Point {
    const rect = this.clientRect();
    return {
      x: rect.left,
      y: rect.top,
    };
  }

  delay(ms: number, fn: Function = null): this {
    this.cancelDelay();
    if (fn) {
      if (ms === 0) fn();
      else this._delayId = setTimeout(fn, ms);
    }
    return this;
  }

  cancelDelay() {
    clearTimeout(this._delayId);
    return this;
  }

  show(delay: number = 0): number {
    return Ticker.setTimeout(() => {
      this.addClass('is_visible');
    }, delay);
  }

  fade(visible: boolean, options: { delay?: number, complete?: Function, hideDulation?: number } = {}) {
    const delay = options.delay !== undefined ? options.delay : 0;
    const complete = options.complete !== undefined ? options.complete : null;
    const hideDulation = options.hideDulation !== undefined ? options.hideDulation : 500;
    this.delay(delay, () => {
      if (visible) {
        this.addClass('is_visible');
        this.delay(33, () => {
          this.addClass('is_show');
          if (complete) complete();
        });
      } else {
        this.removeClass('is_show');
        this.delay(hideDulation, () => {
          this.removeClass('is_visible');
          if (complete) complete();
        });
      }
    });
    return this;
  }

  findParentNode(targetNodes: string[], maxDepth = 5) {
    let depth = 0;
    const find = (el) => {
      if (!el) return null;
      const name = el.nodeName.toLowerCase();
      if (targetNodes.includes(name)) return el;
      depth++;
      if (depth > maxDepth) return null;
      return find(el.parentNode);
    };
    return find(this._el);
  }
}
